// src/services/payment/ebicsVerbindungService.ts
//
// Dienst für den Bereich "Bankverbindung (EBICS)" im Zahlungsabgleich
// (XTAX GmbH & Co. KG, Bankumsatzabruf Kreissparkasse Köln, 04.11.2026):
// Initialisierung und Briefe (Stufe 1), Bankschlüssel (Stufe 2), Abruf der
// Tagesauszüge von Hand (Stufe 3).
// Alle Aufrufe laufen über die Netlify-Funktion ebics-verbindung; das
// Frontend hat keinen direkten Zugriff auf die Tabelle ebics_teilnehmer.

import { netlifyFetch } from '../../shared/services/netlifyFetch';

export type EbicsStatus = 'neu' | 'schluessel_erzeugt' | 'ini_gesendet' | 'initialisiert' | 'bereit';

export interface EbicsTeilnehmer {
  bank_name: string;
  iban: string;
  kontoinhaber: string;
  host_id: string;
  partner_id: string;
  user_id: string;
  teilnehmer_name: string;
  status: EbicsStatus;
  schluessel_erzeugt_am: string | null;
  ini_gesendet_am: string | null;
  ini_auftragsnummer: string | null;
  hia_gesendet_am: string | null;
  hia_auftragsnummer: string | null;
  bankschluessel_geprueft_am: string | null;
  letzter_abruf_am: string | null;
  letzter_abruf_ergebnis: string | null;
  letzte_rueckmeldung: string | null;
  letzter_fehler: string | null;
  laeuft_gerade: boolean;
}

export interface EbicsErgebnis {
  success: boolean;
  error?: string;
  meldung?: string | null;
  teilnehmer?: EbicsTeilnehmer;
}

async function rufeAuf(aktion: string, zusatz: Record<string, unknown> = {}): Promise<Record<string, unknown> & { success: boolean; error?: string }> {
  try {
    const response = await netlifyFetch('/.netlify/functions/ebics-verbindung', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ aktion, ...zusatz })
    });
    const daten = await response.json();
    if (!response.ok || !daten.success) {
      return { ...daten, success: false, error: daten.error || `Anfrage fehlgeschlagen (HTTP ${response.status})` };
    }
    return daten;
  } catch (error) {
    console.error(`[EbicsVerbindung] ${aktion}:`, error);
    return { success: false, error: error instanceof Error ? error.message : 'Unbekannter Fehler' };
  }
}

export const ebicsVerbindungService = {

  /** Teilnehmerdaten und Stand der Initialisierung. */
  ladeStatus: async (): Promise<EbicsErgebnis> => {
    const daten = await rufeAuf('status');
    return {
      success: daten.success,
      error: daten.error,
      teilnehmer: daten.teilnehmer as EbicsTeilnehmer | undefined
    };
  },

  /**
   * Erzeugt beim ersten Aufruf die Teilnehmerschlüssel und sendet INI und HIA
   * an die Bank. Ein weiterer Aufruf setzt nur einen unterbrochenen Lauf fort.
   */
  initialisiere: async (): Promise<EbicsErgebnis> => {
    const daten = await rufeAuf('initialisieren');
    return {
      success: daten.success,
      error: daten.error,
      teilnehmer: daten.teilnehmer as EbicsTeilnehmer | undefined
    };
  },

  /**
   * Holt die Bankschlüssel (HPB) und prüft sie gegen das Bankparameterblatt.
   * Erst danach ist der Abruf der Tagesauszüge möglich.
   */
  holeBankschluessel: async (): Promise<EbicsErgebnis> => {
    const daten = await rufeAuf('bankschluessel');
    return {
      success: daten.success,
      error: daten.error,
      teilnehmer: daten.teilnehmer as EbicsTeilnehmer | undefined
    };
  },

  /**
   * Ruft die Tagesauszüge sofort ab und ordnet sie zu, wie der tägliche Lauf.
   * Mit Zeitraum (JJJJ-MM-TT) fordert die Bank die Auszüge dieses Zeitraums
   * erneut an, auch bereits abgeholte.
   */
  rufeTagesauszuegeAb: async (zeitraum?: { von: string; bis: string }): Promise<EbicsErgebnis> => {
    const daten = await rufeAuf('abrufen', zeitraum ? { zeitraum } : {});
    return {
      success: daten.success,
      error: daten.error,
      meldung: (daten.meldung as string | null | undefined) ?? null,
      teilnehmer: daten.teilnehmer as EbicsTeilnehmer | undefined
    };
  },

  /** Lädt die Initialisierungsbriefe INI und HIA als PDF herunter. */
  ladeBriefeHerunter: async (): Promise<{ success: boolean; error?: string }> => {
    const daten = await rufeAuf('briefe');
    if (!daten.success) return { success: false, error: daten.error };

    const base64 = String(daten.pdf || '');
    const dateiname = String(daten.dateiname || 'EBICS-Initialisierungsbriefe.pdf');
    const binaer = atob(base64);
    const bytes = new Uint8Array(binaer.length);
    for (let i = 0; i < binaer.length; i += 1) bytes[i] = binaer.charCodeAt(i);

    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = dateiname;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return { success: true };
  }
};
